import React from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import type { PackageType } from '../../hooks/useSignupForm';

interface SignupInvestmentSummaryProps {
  basePrice: number;
  packageType: PackageType;
  contractedClientCapacity: number;
  contractedNucleusCapacity: number;
  extraUsersCount: number;
  calculateClientCapacityCost: (capacity: number) => number;
  calculateNucleusCapacityCost: (capacity: number) => number;
  calculateCustomExtraUsersCost: (extraUsers: number) => number;
}

/**
 * Resumo do investimento mensal inicial estimado, no fim do formulário de cadastro. Extraído de
 * SignupForm.tsx (F7).
 */
export const SignupInvestmentSummary: React.FC<SignupInvestmentSummaryProps> = ({
  basePrice,
  packageType,
  contractedClientCapacity,
  contractedNucleusCapacity,
  extraUsersCount,
  calculateClientCapacityCost,
  calculateNucleusCapacityCost,
  calculateCustomExtraUsersCost,
}) => {
  let total = basePrice;
  if (packageType === 'gestao' && contractedClientCapacity > 0) total += calculateClientCapacityCost(contractedClientCapacity);
  if (packageType === 'institucional' && contractedNucleusCapacity > 0) total += calculateNucleusCapacityCost(contractedNucleusCapacity);
  if (packageType === 'institucional' && extraUsersCount > 0) total += calculateCustomExtraUsersCost(extraUsersCount);

  return (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Investimento mensal inicial (estimado)
          </Typography>

          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
            {basePrice > 0 && (
              <Typography variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                Mensalidade fixa: <strong>R$ {basePrice.toFixed(2)}</strong>
              </Typography>
            )}

            {packageType === 'gestao' && contractedClientCapacity > 0 && (
              <Typography variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                Capacidade de condomínios ({contractedClientCapacity}):{' '}
                <strong>R$ {calculateClientCapacityCost(contractedClientCapacity).toFixed(2)}</strong>
              </Typography>
            )}

            {packageType === 'institucional' && contractedNucleusCapacity > 0 && (
              <Typography variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                Capacidade de núcleos ({contractedNucleusCapacity}):{' '}
                <strong>R$ {calculateNucleusCapacityCost(contractedNucleusCapacity).toFixed(2)}</strong>
              </Typography>
            )}

            {packageType === 'institucional' && extraUsersCount > 0 && (
              <Typography variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                Usuários extras ({extraUsersCount}):{' '}
                <strong>R$ {calculateCustomExtraUsersCost(extraUsersCount).toFixed(2)}</strong>
              </Typography>
            )}

            {packageType === 'condominio' && (
              <Typography variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                Valor por unidades: <strong>calculado ao cadastrar o condomínio</strong> (mínimo + faixas por unidade).
              </Typography>
            )}

            <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
              {packageType === 'condominio' ? 'Subtotal' : 'Total'}: R$ {total.toFixed(2)}/mês
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default SignupInvestmentSummary;
